import { useId, type ReactElement } from 'react';
import { ErrorCode } from '@gr/core/errors';
import type { MediaStorageDto } from '@gr/core/content/media/recompress';
import type { TaskDto } from '@gr/core/platform/tasks/dto';
import { useServices, useT } from '../../../app/di';
import { formatFileSize } from '../../../shared/format';
import { Button } from '../../../ui-kit/Button/Button';
import { useConfirm } from '../../../ui-kit/ConfirmDialog/ConfirmDialog';
import { Dialog } from '../../../ui-kit/Dialog/Dialog';
import type { ServerOutcome } from '../../../ui-kit/form/server-errors';
import { useBlog } from '../../accounts/workspaces';
import { recompressParams, selectionSummary, type RecompressFormValues } from './model/recompress-form';
import { RecompressForm } from './RecompressForm/RecompressForm';

export interface RecompressDialogProps {
  storage: MediaStorageDto;
  onClose(): void;
  /** Задача поставлена (TASK-UC-1): дальше её ход — в индикаторе фоновых задач. */
  onStarted(task: TaskDto): void;
}

// MED-UC-9, MED-UC-9.CC-1: параметры пережатия, подтверждение необратимого действия и постановка задачи.
export function RecompressDialog({ storage, onClose, onStarted }: RecompressDialogProps): ReactElement {
  const { api } = useServices();
  const { t, tp, locale } = useT();
  const confirm = useConfirm();
  const { blog } = useBlog();
  const formId = useId();

  const submit = async (values: RecompressFormValues): Promise<ServerOutcome> => {
    const summary = selectionSummary(storage, values);
    if (summary.files === 0) return { success: false, messages: [ErrorCode.MediaRecompressNothing] };
    const confirmed = await confirm({
      title: t('media.recompress.confirmTitle', { files: tp('media.storage.files', summary.files, { n: summary.files }), size: formatFileSize(summary.bytes, locale) }),
      text: t('media.recompress.confirmText'),
      confirmLabel: t('media.recompress.confirm'),
      danger: true,
    });
    if (!confirmed) return { success: true, messages: [] };
    const result = await api.media.recompress(blog.id, recompressParams(storage, values));
    if (result.success) {
      onStarted(result.data);
      onClose();
    }
    return result;
  };

  return (
    <Dialog
      open
      wide
      title={t('media.recompress.title')}
      closeLabel={t('common.close')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" type="submit" form={formId}>
            {t('media.recompress.submit')}
          </Button>
        </>
      }
    >
      <RecompressForm id={formId} storage={storage} onSubmit={submit} />
    </Dialog>
  );
}
